import { useState, useEffect } from "react";
import useLearningPlatform from "../../../api/learningPlatform";
import { Globe, Copy, Check, Loader2, ExternalLink } from "lucide-react";
import {
  FormField,
  inputClass,
  buttonClass,
  SectionLoader,
  isNotFoundError,
} from "./shared";

const ROOT_DOMAIN = "leerplatform.zichtbaarstudio.be";

const DEFAULT = { subdomain: "", custom_domain: "" };

const DomainTab = ({ isDark }) => {
  const { getSettings, updateSettings } = useLearningPlatform();
  const [values, setValues] = useState(DEFAULT);
  const [original, setOriginal] = useState(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getSettings();
        if (cancelled) return;
        const merged = {
          subdomain: data?.subdomain || "",
          custom_domain: data?.custom_domain || "",
        };
        setValues(merged);
        setOriginal(merged);
      } catch (e) {
        if (!isNotFoundError(e)) setError("Kon instellingen niet laden");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getSettings]);

  const dirty = JSON.stringify(values) !== JSON.stringify(original);

  const update = (patch) => {
    setValues((prev) => ({ ...prev, ...patch }));
    setSavedAt(null);
    setError(null);
  };

  const validateSubdomain = (value) => {
    if (!value) return "";
    if (!/^[a-z0-9-]+$/.test(value)) {
      return "Alleen kleine letters, cijfers en streepjes toegestaan";
    }
    if (value.startsWith("-") || value.endsWith("-")) {
      return "Mag niet beginnen of eindigen met een streepje";
    }
    return "";
  };

  const subdomainError = validateSubdomain(values.subdomain);

  const handleSave = async () => {
    if (subdomainError) return;
    setSaving(true);
    setError(null);
    try {
      const data = await updateSettings(values);
      const merged = {
        subdomain: data?.subdomain || "",
        custom_domain: data?.custom_domain || "",
      };
      setValues(merged);
      setOriginal(merged);
      setSavedAt(Date.now());
    } catch (e) {
      const msg = e?.response?.data?.message || "Kon niet opslaan";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch (e) {
      // ignore
    }
  };

  if (loading) return <SectionLoader isDark={isDark} />;

  const subdomainUrl = values.subdomain
    ? `https://${values.subdomain}.${ROOT_DOMAIN}`
    : null;
  const customUrl = values.custom_domain
    ? `https://${values.custom_domain}`
    : null;

  return (
    <div className="space-y-6 max-w-2xl">
      <section className="space-y-3">
        <SectionHeader
          isDark={isDark}
          title="Subdomein"
          description={`Standaard URL: {naam}.${ROOT_DOMAIN}`}
        />
        <FormField label="Subdomein" isDark={isDark}>
          <div className="flex items-stretch">
            <input
              type="text"
              value={values.subdomain}
              onChange={(e) =>
                update({ subdomain: e.target.value.toLowerCase().trim() })
              }
              placeholder="acme"
              className={`${inputClass(isDark)} rounded-r-none`}
            />
            <span
              className={`flex items-center px-3 text-[12px] border border-l-0 rounded-r-md ${
                isDark
                  ? "bg-white/[0.02] border-white/[0.08] text-gray-500"
                  : "bg-gray-50 border-gray-200 text-gray-500"
              }`}
            >
              .{ROOT_DOMAIN}
            </span>
          </div>
          {subdomainError && (
            <p className="text-[11px] text-red-500 mt-1">{subdomainError}</p>
          )}
        </FormField>

        {subdomainUrl && !subdomainError && (
          <UrlPreview
            isDark={isDark}
            url={subdomainUrl}
            copied={copied === "sub"}
            onCopy={() => copy(subdomainUrl, "sub")}
          />
        )}
      </section>

      <section className="space-y-3">
        <SectionHeader
          isDark={isDark}
          title="Eigen domein"
          description="Optioneel — koppel een eigen domein zoals academy.acme.com."
        />
        <FormField label="Eigen domein" isDark={isDark}>
          <input
            type="text"
            value={values.custom_domain}
            onChange={(e) =>
              update({ custom_domain: e.target.value.toLowerCase().trim() })
            }
            placeholder="academy.acme.com"
            className={inputClass(isDark)}
          />
        </FormField>

        {customUrl && (
          <UrlPreview
            isDark={isDark}
            url={customUrl}
            copied={copied === "custom"}
            onCopy={() => copy(customUrl, "custom")}
          />
        )}

        {values.custom_domain && values.subdomain && !subdomainError && (
          <CnameInstructions
            isDark={isDark}
            customDomain={values.custom_domain}
            target={`${values.subdomain}.${ROOT_DOMAIN}`}
            copied={copied === "cname"}
            onCopy={() =>
              copy(`${values.subdomain}.${ROOT_DOMAIN}`, "cname")
            }
          />
        )}
      </section>

      {error && (
        <div
          className={`text-[12px] px-3 py-2 rounded-md ${
            isDark ? "bg-red-500/10 text-red-400" : "bg-red-50 text-red-600"
          }`}
        >
          {error}
        </div>
      )}

      <div
        className={`flex items-center justify-end gap-2 pt-2 border-t ${
          isDark ? "border-white/[0.06]" : "border-gray-100"
        }`}
      >
        {savedAt && !dirty && (
          <span
            className={`flex items-center gap-1 text-[12px] mr-auto ${
              isDark ? "text-emerald-400" : "text-emerald-600"
            }`}
          >
            <Check size={13} /> Opgeslagen
          </span>
        )}
        <button
          onClick={() => {
            setValues(original);
            setError(null);
            setSavedAt(null);
          }}
          disabled={!dirty || saving}
          className={buttonClass(isDark, "ghost")}
        >
          Annuleer
        </button>
        <button
          onClick={handleSave}
          disabled={!dirty || saving || !!subdomainError}
          className={buttonClass(isDark, "primary")}
        >
          {saving && <Loader2 size={13} className="animate-spin" />}
          Opslaan
        </button>
      </div>
    </div>
  );
};

const SectionHeader = ({ isDark, title, description }) => (
  <div className="flex items-start gap-2">
    <Globe
      size={14}
      className={`mt-0.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
    />
    <div>
      <h3
        className={`text-[13px] font-semibold ${
          isDark ? "text-gray-200" : "text-gray-800"
        }`}
      >
        {title}
      </h3>
      <p
        className={`text-[11px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
      >
        {description}
      </p>
    </div>
  </div>
);

const UrlPreview = ({ isDark, url, copied, onCopy }) => (
  <div
    className={`flex items-center gap-2 px-3 py-2 rounded-md text-[12px] font-mono ${
      isDark ? "bg-white/[0.03] text-gray-300" : "bg-gray-50 text-gray-700"
    }`}
  >
    <span className="flex-1 truncate">{url}</span>
    <button
      onClick={onCopy}
      className={`p-1 rounded transition-colors ${
        isDark
          ? "text-gray-500 hover:text-gray-200"
          : "text-gray-400 hover:text-gray-700"
      }`}
      title="Kopieer"
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
    </button>
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className={`p-1 rounded transition-colors ${
        isDark
          ? "text-gray-500 hover:text-gray-200"
          : "text-gray-400 hover:text-gray-700"
      }`}
      title="Open"
    >
      <ExternalLink size={12} />
    </a>
  </div>
);

const CnameInstructions = ({ isDark, customDomain, target, copied, onCopy }) => (
  <div
    className={`p-3 rounded-md text-[12px] ${
      isDark
        ? "bg-blue-500/[0.06] border border-blue-500/20 text-gray-300"
        : "bg-blue-50 border border-blue-100 text-gray-700"
    }`}
  >
    <p className="font-medium mb-1.5">DNS-configuratie</p>
    <p
      className={`mb-2 leading-relaxed ${
        isDark ? "text-gray-400" : "text-gray-600"
      }`}
    >
      Maak bij je DNS-provider een CNAME-record voor{" "}
      <strong>{customDomain}</strong> die wijst naar:
    </p>
    <div
      className={`flex items-center gap-2 px-2.5 py-1.5 rounded font-mono text-[11px] ${
        isDark ? "bg-black/40" : "bg-white"
      }`}
    >
      <span className="flex-1">{target}</span>
      <button
        onClick={onCopy}
        className={`p-0.5 rounded transition-colors ${
          isDark
            ? "text-gray-500 hover:text-gray-200"
            : "text-gray-400 hover:text-gray-700"
        }`}
      >
        {copied ? <Check size={11} /> : <Copy size={11} />}
      </button>
    </div>
    <p
      className={`mt-2 text-[11px] ${
        isDark ? "text-gray-500" : "text-gray-500"
      }`}
    >
      DNS-wijzigingen kunnen tot een uur duren om actief te worden.
    </p>
  </div>
);

export default DomainTab;
